import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useLanguage } from '../../i18n';
import {
  Box,
  Button,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  alpha,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  CheckCircle as SuccessIcon,
  PlayArrow as ProcessIcon,
  Receipt as ReceiptIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  Description as DocIcon,
} from '@mui/icons-material';
import { uploadReceipt, processReceipt, approveReceipt, rejectReceipt, uploadToFirebase, processDocument } from '../../services/api';
import { ReceiptValidation, ReceiptStatus, ExtractedData, FieldValue, ComparisonResult, Classification } from '../../types';
import FirebaseSelector, { ReprocessResult } from '../shared/FirebaseSelector';
import DocumentPreview from '../shared/DocumentPreview';
import ProcessingStatus, { ProcessingStep as PSProcessingStep } from '../ProcessingStatus';

// Receipt data interface
interface ReceiptData {
  receipt_id: string;
  receipt_index: number;
  fields: Record<string, FieldValue>;
  validation: ReceiptValidation;
  status_recommendation: ReceiptStatus;
  total_fields: number;
}

// Processing steps — same as Tab 1
const initialSteps: PSProcessingStep[] = [
  { label: 'Document Agent', description: 'Uploading document and preparing for pipeline processing', status: 'pending' },
  { label: 'OCR Extraction Agent', description: 'Extracting text and structured fields using Document AI + Gemini', status: 'pending' },
  { label: 'ERP Comparison Agent', description: 'Matching extracted fields against ERP records from data bucket', status: 'pending' },
  { label: 'Classification Agent', description: 'Analyzing results and classifying as standard or exception case', status: 'pending' },
];

function UnloadingReceiptUpload() {
  const { t } = useLanguage();
  const [file, setFile] = useState<File | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [uploading, setUploading] = useState(false);
  const [validation, setValidation] = useState<ReceiptValidation | null>(null);
  const [statusRec, setStatusRec] = useState<ReceiptStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [receiptId, setReceiptId] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [receipts, setReceipts] = useState<ReceiptData[]>([]);
  const [processingSteps, setProcessingSteps] = useState<PSProcessingStep[]>(initialSteps);
  const [showSteps, setShowSteps] = useState(true);
  const [typewriterText, setTypewriterText] = useState('');
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult[] | null>(null);
  const [classification, setClassification] = useState<Classification | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);

  // Processing status state (same as Tab 1)
  const [currentStep, setCurrentStep] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isProcessingActive, setIsProcessingActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Timer effect
  useEffect(() => {
    if (isProcessingActive) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isProcessingActive]);

  // Typewriter effect for processing messages
  useEffect(() => {
    if (currentStepIndex >= 0 && currentStepIndex < processingSteps.length) {
      const step = processingSteps[currentStepIndex];
      if (step.status === 'running') {
        const text = step.description;
        let index = 0;
        setTypewriterText('');
        const interval = setInterval(() => {
          if (index < text.length) {
            setTypewriterText(text.substring(0, index + 1));
            index++;
          } else {
            clearInterval(interval);
          }
        }, 30);
        return () => clearInterval(interval);
      }
    }
  }, [currentStepIndex, processingSteps]);

  // Update step status helper
  const updateStep = (stepIndex: number, status: PSProcessingStep['status'], duration?: number) => {
    setProcessingSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status, duration } : step
    ));
    if (status === 'running') setCurrentStep(stepIndex);
  };

  const resetSteps = () => {
    setProcessingSteps(initialSteps.map(s => ({ ...s })));
    setCurrentStep(0);
    setElapsedTime(0);
    setIsProcessingActive(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
      setError(null);
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError(null);
    }
  }, []);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError(null);
    setMessage(null);
    resetSteps();
    setReceipts([]);
    setExtractedData(null);
    setValidation(null);
    setStatusRec(null);
    setReceiptId(null);
    setComparisonResult(null);
    setClassification(null);
    setIsProcessingActive(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (orderNumber) {
        formData.append('delivery_number', orderNumber);
      }

      // Step 1: Upload to Firebase & OCR Extract
      const step1Start = Date.now();
      updateStep(0, 'running');

      console.log('[Receipt] Calling uploadToFirebase...');
      const uploadResponse = await uploadToFirebase(formData);
      console.log('[Receipt] Upload response:', uploadResponse);

      const step1Duration = Math.round((Date.now() - step1Start) / 1000);
      updateStep(0, 'completed', step1Duration);

      // Step 2: OCR Processing
      updateStep(1, 'running');
      await new Promise(resolve => setTimeout(resolve, 400));
      updateStep(1, 'completed', 1);

      // Step 3: Field Extraction
      updateStep(2, 'running');
      await new Promise(resolve => setTimeout(resolve, 400));
      updateStep(2, 'completed', 1);

      // Set extracted data from upload response
      const docId = uploadResponse.document_id;
      setDocumentId(docId);
      setReceiptId(docId);

      const extractedDataWithDefaults = {
        ...uploadResponse.extracted_data,
        all_fields: uploadResponse.extracted_data?.all_fields || uploadResponse.extracted_data?.field_details || {},
        field_details: uploadResponse.extracted_data?.field_details || uploadResponse.extracted_data?.all_fields || {},
        detected_language: uploadResponse.extracted_data?.detected_language || 'unknown',
        total_fields_extracted: uploadResponse.extracted_data?.total_fields_extracted ||
          Object.keys(uploadResponse.extracted_data?.all_fields || uploadResponse.extracted_data?.field_details || {}).length
      };
      setExtractedData(extractedDataWithDefaults as ExtractedData);

      // Step 4: Language Detection
      updateStep(3, 'running');
      await new Promise(resolve => setTimeout(resolve, 300));
      updateStep(3, 'completed', 1);

      // Step 5: ERP Comparison via processDocument (same as Tab 1)
      updateStep(4, 'running');
      try {
        console.log('[Receipt] Processing document for ERP comparison:', docId);
        const processResponse = await processDocument(docId);
        console.log('[Receipt] Process response:', processResponse);

        if (processResponse.comparison_result) {
          setComparisonResult(processResponse.comparison_result);
        }
        if (processResponse.classification) {
          setClassification(processResponse.classification);
        }
        updateStep(4, 'completed', 1);
      } catch (processErr) {
        console.warn('[Receipt] ERP comparison failed (extraction still succeeded):', processErr);
        updateStep(4, 'completed', 1);
      }

      // Step 6: Status Recommendation
      updateStep(5, 'running');
      await new Promise(resolve => setTimeout(resolve, 300));
      updateStep(5, 'completed', 1);

      setMessage({ type: 'success', text: '✅ Receipt uploaded, extracted & compared with ERP successfully' });
    } catch (err: any) {
      console.error('Upload error:', err);
      const failedStepIndex = processingSteps.findIndex(s => s.status === 'running');
      if (failedStepIndex >= 0) updateStep(failedStepIndex, 'error');
      setError(err.response?.data?.detail || err.message || 'Upload failed');
      setMessage({ type: 'error', text: err.response?.data?.detail || err.message || 'Upload failed' });
    } finally {
      setUploading(false);
      setIsProcessingActive(false);
    }
  };

  const handleProcess = async () => {
    if (!receiptId) return;

    setUploading(true);
    try {
      const result = await processReceipt(receiptId);
      setStatusRec(result.status_recommendation);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Processing failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
          }}
        >
          <ReceiptIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {t.receiptPage.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.receiptPage.subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Firebase Document Selector & ERP Data — wraps upload area as children */}
      <FirebaseSelector
        processType="unloading"
        disabled={uploading}
        onReprocessComplete={(docId: string, result: ReprocessResult) => {
          console.log('[UnloadingReceipt] Reprocess complete:', docId, result);
          setReceiptId(docId);
          if (result.extracted_data) {
            const data = result.extracted_data;
            setExtractedData({
              all_fields: data.all_fields || data.field_details || {},
              field_details: data.field_details || data.all_fields || {},
              total_fields_extracted: data.total_fields_extracted ||
                Object.keys(data.all_fields || data.field_details || {}).length,
              detected_language: data.detected_language || 'unknown',
            } as ExtractedData);
          }
          if (result.comparison_result) {
            setValidation({
              receipt_id: docId,
              is_valid: !result.is_exception,
              missing_fields: [],
              invalid_fields: [],
              validation_errors: result.exception_reasons || [],
            });
          }
          if (result.classification) {
            setStatusRec({
              receipt_id: docId,
              status: result.is_exception ? 'pending' : 'approved',
              confidence: result.classification.confidence || 0.9,
              reasoning: result.classification.reason || 'Reprocessed with ERP comparison',
              rules_applied: [],
              flags: result.exception_reasons || [],
            });
          }
          setMessage({ type: 'success', text: '✅ Document reprocessed with ERP comparison' });
        }}
      >
        {/* Divider between Firebase selector and upload */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Divider sx={{ flex: 1 }} />
          <Chip
            label="Or upload new"
            size="small"
            variant="outlined"
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              letterSpacing: '0.05em',
              color: 'text.secondary',
              borderColor: 'divider',
            }}
          />
          <Divider sx={{ flex: 1 }} />
        </Box>

        {/* Order Number Input */}
        <TextField
          label={t.receiptPage.orderNumber}
          placeholder={t.receiptPage.orderNumberPlaceholder}
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 3 }}
        />

        {/* Drag & Drop Upload Area */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: dragActive ? 'success.main' : file ? 'success.main' : 'divider',
            borderRadius: 3,
            p: 4,
            textAlign: 'center',
            backgroundColor: dragActive ? alpha('#10B981', 0.04) : file ? alpha('#10B981', 0.04) : 'transparent',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            '&:hover': {
              borderColor: 'success.main',
              backgroundColor: alpha('#10B981', 0.04),
              transform: 'scale(1.01)',
            },
          }}
        >
          <input
            accept="image/*,.pdf"
            style={{ display: 'none' }}
            id="receipt-file-input"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="receipt-file-input" style={{ cursor: 'pointer', display: 'block' }}>
            {file ? (
              <Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    backgroundColor: alpha('#10B981', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <FileIcon sx={{ fontSize: 32, color: '#10B981' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {file.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(file.size)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: 2,
                    backgroundColor: alpha('#10B981', 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <UploadIcon sx={{ fontSize: 32, color: '#10B981' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {dragActive ? t.receiptPage.dropReceipt : t.receiptPage.dragDropReceipt}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t.receiptPage.browseFormats}
                </Typography>
              </Box>
            )}
          </label>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3, alignItems: 'center' }}>
          {file && <DocumentPreview file={file} compact />}
          <Button
            variant="contained"
            size="large"
            onClick={handleUpload}
            disabled={!file || uploading}
            startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
            sx={{
              flex: 1,
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(16, 185, 129, 0.4)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {uploading ? t.receiptPage.uploading : t.receiptPage.uploadReceipt}
          </Button>
          {receiptId && (
            <Button
              variant="contained"
              size="large"
              onClick={handleProcess}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <ProcessIcon />}
              sx={{
                flex: 1,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(139, 92, 246, 0.4)',
                },
              }}
            >
              {uploading ? t.common.processing : t.receiptPage.processReceipt}
            </Button>
          )}
        </Box>
      </FirebaseSelector>

      {/* Processing Status with Agent Steps — same as Tab 1 */}
      <ProcessingStatus
        isProcessing={isProcessingActive}
        currentStep={currentStep}
        steps={processingSteps}
        elapsedTime={elapsedTime}
        estimatedTime={120}
      />

      {/* Extracted Data Display */}
      {extractedData && (extractedData.all_fields || extractedData.field_details) && (
        <Paper
          sx={{
            mt: 3,
            p: 3,
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
            border: '1px solid',
            borderColor: 'primary.main',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t.receiptPage.extractedData}
            </Typography>
            {extractedData.total_fields_extracted && (
              <Chip
                label={`${extractedData.total_fields_extracted} ${t.receiptPage.fields}`}
                size="small"
                color="primary"
              />
            )}
            {extractedData.detected_language && (
              <Chip
                label={`Language: ${extractedData.detected_language.toUpperCase()}`}
                size="small"
                variant="outlined"
                color="primary"
              />
            )}
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#3B82F6', 0.1) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Language</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Original Text</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>English Text</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Normalized Value</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(extractedData.all_fields || extractedData.field_details || {}).map(([key, fieldValue]) => {
                  const value = fieldValue as FieldValue;
                  const displayValue = value?.normalized || value?.translated || value?.english_text || value?.original || '';
                  const originalText = value?.original_text || value?.original || '';
                  const englishText = value?.english_text || value?.translated || '';
                  const language = value?.detected_language || extractedData.detected_language || 'N/A';
                  
                  return (
                    <TableRow key={key} sx={{ '&:hover': { backgroundColor: alpha('#3B82F6', 0.05) } }}>
                      <TableCell>
                        <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip label={language.toUpperCase()} size="small" />
                      </TableCell>
                      <TableCell>{originalText || 'N/A'}</TableCell>
                      <TableCell>{englishText || 'N/A'}</TableCell>
                      <TableCell>
                        <strong>{displayValue || 'N/A'}</strong>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const conf = (value as any)?.confidence;
                          if (conf == null) return <Chip label="N/A" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
                          const pct = Math.round(conf * 100);
                          const color = conf >= 0.8 ? '#10B981' : conf >= 0.5 ? '#F59E0B' : '#EF4444';
                          return <Chip label={`${pct}%`} size="small" sx={{ backgroundColor: alpha(color, 0.15), color, fontWeight: 700, fontSize: '0.75rem', minWidth: 48 }} />;
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* 📊 Comparison with ERP Data — LLM-based (same as Tab 1) */}
      {comparisonResult && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              📊 Comparison with ERP Data
            </Typography>
            {(() => {
              const noDataCount = comparisonResult.filter(r => !r.document_value && !r.erp_value).length;
              const withDataResults = comparisonResult.filter(r => r.document_value || r.erp_value);
              const matchCount = withDataResults.filter(r => r.match).length;
              const totalCount = withDataResults.length;
              const matchPct = totalCount > 0 ? Math.round((matchCount / totalCount) * 100) : 0;
              return (
                <>
                  <Chip
                    label={`${matchCount}/${totalCount} fields match`}
                    size="small"
                    color={matchPct === 100 ? 'success' : matchPct >= 50 ? 'warning' : 'error'}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    label={`${matchPct}% match rate`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444',
                      color: matchPct === 100 ? '#10B981' : matchPct >= 50 ? '#F59E0B' : '#EF4444',
                      fontWeight: 600,
                    }}
                  />
                </>
              );
            })()}
          </Box>
          <TableContainer sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'background.paper',
            overflow: 'hidden',
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: alpha('#8B5CF6', 0.1) }}>
                  <TableCell sx={{ fontWeight: 700, width: '20%' }}>Field</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>📄 Document Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '25%' }}>🏢 ERP Value</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%', textAlign: 'center' }}>Match Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '15%' }}>Discrepancy</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {comparisonResult.map((result) => {
                  const bothEmpty = !result.document_value && !result.erp_value;
                  const isMatch = result.match && !bothEmpty;
                  const isMismatch = !result.match && !bothEmpty;
                  const isNoData = bothEmpty;

                  return (
                  <TableRow
                    key={result.field}
                    sx={{
                      backgroundColor: isNoData
                        ? alpha('#6B7280', 0.03)
                        : isMatch
                          ? alpha('#10B981', 0.03)
                          : alpha('#EF4444', 0.05),
                      '&:hover': {
                        backgroundColor: isNoData
                          ? alpha('#6B7280', 0.08)
                          : isMatch
                            ? alpha('#10B981', 0.08)
                            : alpha('#EF4444', 0.1),
                      },
                      transition: 'background-color 0.2s ease',
                    }}
                  >
                    <TableCell>
                      <strong>{result.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
                    </TableCell>
                    <TableCell sx={{
                      color: result.document_value ? 'text.primary' : 'text.disabled',
                      fontFamily: result.document_value ? 'monospace' : 'inherit',
                    }}>
                      {result.document_value || '—  Not found'}
                    </TableCell>
                    <TableCell sx={{
                      color: result.erp_value ? 'text.primary' : 'text.disabled',
                      fontFamily: result.erp_value ? 'monospace' : 'inherit',
                    }}>
                      {result.erp_value || '—  Not available'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      {isNoData ? (
                        <Chip label="⚠ No Data" size="small" sx={{ backgroundColor: alpha('#6B7280', 0.15), color: '#9CA3AF', fontWeight: 700, fontSize: '0.75rem' }} />
                      ) : isMatch ? (
                        <Chip label="✓ Match" size="small" sx={{ backgroundColor: alpha('#10B981', 0.15), color: '#10B981', fontWeight: 700, fontSize: '0.75rem' }} />
                      ) : (
                        <Chip label="✗ Mismatch" size="small" sx={{ backgroundColor: alpha('#EF4444', 0.15), color: '#EF4444', fontWeight: 700, fontSize: '0.75rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {isMatch ? (
                        <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700, fontSize: '1rem' }}>✓</Typography>
                      ) : isNoData ? (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      ) : result.discrepancy_type ? (
                        <Chip label={result.discrepancy_type.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 22, borderColor: alpha('#F59E0B', 0.6), color: '#F59E0B' }} />
                      ) : (
                        <Typography variant="caption" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Success/Error Message */}
      {message && (
        <Alert severity={message.type} sx={{ mt: 3 }}>
          {message.text}
        </Alert>
      )}

      {/* Error Message */}
      {error && !message && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}

      {/* Validation Result */}
      {validation && (
        <Paper
          sx={{
            mt: 3,
            p: 3,
            background: validation.is_valid
              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)'
              : 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)',
            border: '1px solid',
            borderColor: validation.is_valid ? 'success.main' : 'warning.main',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SuccessIcon sx={{ color: validation.is_valid ? '#10B981' : '#F59E0B', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t.receiptPage.validationResult}
            </Typography>
            <Chip
              label={validation.is_valid ? t.receiptPage.valid : t.receiptPage.needsReview}
              size="small"
              sx={{
                backgroundColor: validation.is_valid ? '#10B981' : '#F59E0B',
                color: 'white',
                fontWeight: 600,
              }}
            />
          </Box>
          {!validation.is_valid && validation.validation_errors.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t.receiptPage.issues}: {validation.validation_errors.join(', ')}
            </Typography>
          )}
          {validation.missing_fields.length > 0 && (
            <Typography variant="body2" color="warning.main">
              {t.receiptPage.missingFields}: {validation.missing_fields.join(', ')}
            </Typography>
          )}
        </Paper>
      )}

      {/* Status Recommendation with Actions */}
      {statusRec && (
        <Paper
          sx={{
            mt: 3,
            p: 3,
            background:
              statusRec.status === 'approved'
                ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)'
                : statusRec.status === 'rejected'
                ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.05) 100%)',
            border: '1px solid',
            borderColor:
              statusRec.status === 'approved'
                ? 'success.main'
                : statusRec.status === 'rejected'
                ? 'error.main'
                : 'warning.main',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {statusRec.status === 'approved' && <SuccessIcon sx={{ color: '#10B981', fontSize: 28 }} />}
              {statusRec.status === 'rejected' && <ErrorIcon sx={{ color: '#EF4444', fontSize: 28 }} />}
              {statusRec.status === 'pending' && <PendingIcon sx={{ color: '#F59E0B', fontSize: 28 }} />}
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t.receiptPage.statusRecommendation}
              </Typography>
              <Chip
                label={statusRec.status.toUpperCase()}
                size="small"
                sx={{
                  backgroundColor:
                    statusRec.status === 'approved'
                      ? '#10B981'
                      : statusRec.status === 'rejected'
                      ? '#EF4444'
                      : '#F59E0B',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            </Box>
            
            {/* Manual Approval Actions */}
            {statusRec.status === 'pending' && receiptId && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Approve Receipt">
                  <Button
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={<ApproveIcon />}
                    onClick={async () => {
                      try {
                        await approveReceipt(receiptId);
                        setStatusRec({ ...statusRec, status: 'approved' });
                        setMessage({ type: 'success', text: 'Receipt approved successfully' });
                      } catch (err: any) {
                        setError(err.message || 'Failed to approve receipt');
                      }
                    }}
                    sx={{
                      background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      },
                    }}
                  >
                    Approve
                  </Button>
                </Tooltip>
                <Tooltip title="Reject Receipt">
                  <Button
                    variant="contained"
                    size="small"
                    color="error"
                    startIcon={<RejectIcon />}
                    onClick={async () => {
                      const reason = prompt('Enter rejection reason:');
                      if (reason) {
                        try {
                          await rejectReceipt(receiptId, reason);
                          setStatusRec({ ...statusRec, status: 'rejected' });
                          setMessage({ type: 'info', text: 'Receipt rejected' });
                        } catch (err: any) {
                          setError(err.message || 'Failed to reject receipt');
                        }
                      }
                    }}
                    sx={{
                      background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
                      },
                    }}
                  >
                    Reject
                  </Button>
                </Tooltip>
              </Box>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
            <Typography variant="body1">
              <strong>{t.receiptPage.confidence}:</strong> {(statusRec.confidence * 100).toFixed(1)}%
            </Typography>
            {statusRec.rules_applied && statusRec.rules_applied.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t.receiptPage.rulesApplied}:</strong> {statusRec.rules_applied.length}
              </Typography>
            )}
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>{t.receiptPage.reasoning}:</strong> {statusRec.reasoning}
          </Typography>
          
          {/* Flags */}
          {statusRec.flags && statusRec.flags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {statusRec.flags.map((flag, idx) => (
                <Chip
                  key={idx}
                  label={flag}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Paper>
      )}

    </Box>
  );
}

export default UnloadingReceiptUpload;

